import React, { useState, useEffect } from 'react';
import {
    FiUserCheck, FiEdit2, FiSave, FiX,
    FiClock, FiAlertCircle, FiCheckCircle, FiCalendar
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { db } from '../../../services/firebase';
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';

// Time slots 5PM – 10PM
const ALL_SLOTS = [
    { val: '17:00', label: '5 PM' },
    { val: '18:00', label: '6 PM' },
    { val: '19:00', label: '7 PM' },
    { val: '20:00', label: '8 PM' },
    { val: '21:00', label: '9 PM' },
    { val: '22:00', label: '10 PM' },
];

// Days of the week
const DAYS = [
    { val: 'monday', label: 'Lun' },
    { val: 'tuesday', label: 'Mar' },
    { val: 'wednesday', label: 'Mié' },
    { val: 'thursday', label: 'Jue' },
    { val: 'friday', label: 'Vie' },
    { val: 'saturday', label: 'Sáb' },
    { val: 'sunday', label: 'Dom' },
];

const ALL_DAYS = DAYS.map(d => d.val);

const DEFAULT_EDIT = {
    maxMcPerDay: 2,
    availableSlots: ['18:00', '19:00'],
    availableDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    color: '#10b981',
};

const AsesoresPage = () => {
    const [asesores, setAsesores] = useState([]); // users with isAsesor=true
    const [loading, setLoading] = useState(true);
    const [editingUser, setEditingUser] = useState(null); // user being edited
    const [editForm, setEditForm] = useState(DEFAULT_EDIT);

    useEffect(() => { fetchAsesores(); }, []);

    const fetchAsesores = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(query(collection(db, 'users'), where('isAsesor', '==', true)));
            setAsesores(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error(err);
            toast.error('Error cargando asesores');
        } finally { setLoading(false); }
    };

    const openEdit = (user) => {
        setEditForm({
            maxMcPerDay: user.asesorConfig?.maxMcPerDay ?? 2,
            availableSlots: user.asesorConfig?.availableSlots || ['18:00', '19:00'],
            availableDays: user.asesorConfig?.availableDays || ALL_DAYS.slice(0, 5),
            color: user.asesorConfig?.color || '#10b981',
        });
        setEditingUser(user);
    };

    const saveEdit = async () => {
        try {
            await updateDoc(doc(db, 'users', editingUser.id), {
                asesorConfig: editForm,
            });
            setAsesores(prev => prev.map(a =>
                a.id === editingUser.id ? { ...a, asesorConfig: editForm } : a
            ));
            toast.success('Configuración guardada');
            setEditingUser(null);
        } catch { toast.error('Error guardando'); }
    };

    const toggleSlot = (slot) => setEditForm(p => ({
        ...p,
        availableSlots: p.availableSlots.includes(slot)
            ? p.availableSlots.filter(s => s !== slot)
            : [...p.availableSlots, slot]
    }));

    const toggleDay = (day) => setEditForm(p => ({
        ...p,
        availableDays: p.availableDays.includes(day)
            ? p.availableDays.filter(d => d !== day)
            : [...p.availableDays, day]
    }));

    const slotLabel = (val) => ALL_SLOTS.find(s => s.val === val)?.label || val;
    const dayLabel = (val) => DAYS.find(d => d.val === val)?.label || val;

    const activeAsesores = asesores.filter(a => a.isAsesor);
    const totalCapacity = activeAsesores.reduce((s, a) => s + (a.asesorConfig?.maxMcPerDay ?? 2), 0);

    if (loading) return (
        <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
    );

    return (
        <div className="p-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <FiUserCheck className="text-emerald-600" size={24} />
                    Asesores
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                    Los asesores se activan desde <strong>Admin → Usuarios → toggle Asesor</strong>
                </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                    <p className="text-2xl font-bold text-gray-800">{asesores.length}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Asesores activos</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                    <p className="text-2xl font-bold text-emerald-600">{totalCapacity}</p>
                    <p className="text-xs text-gray-500 mt-0.5">MC máx. por día (total)</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl p-4 text-white shadow-md">
                    <p className="text-2xl font-bold">
                        {asesores.length > 0 ? (totalCapacity / asesores.length).toFixed(1) : '—'}
                    </p>
                    <p className="text-xs opacity-90 mt-0.5">MC promedio/asesor/día</p>
                </div>
            </div>

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                <FiAlertCircle className="text-blue-500 mt-0.5 flex-shrink-0" size={18} />
                <p className="text-sm text-blue-700">
                    Con <strong>{asesores.length} asesores</strong> puedes programar hasta{' '}
                    <strong>{totalCapacity} masterclasses por día</strong>. El calendario
                    mostrará una alerta cuando se supere esta capacidad.
                </p>
            </div>

            {/* Empty state */}
            {asesores.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center shadow-sm">
                    <FiUserCheck size={40} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">No hay asesores configurados</p>
                    <p className="text-gray-400 text-sm mt-2">
                        Ve a <strong>Admin → Usuarios</strong> y activa el toggle <strong>Asesor</strong> para habilitar a un colaborador.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {asesores.map(a => {
                        const cfg = a.asesorConfig || {};
                        return (
                            <div key={a.id}
                                className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-shadow">
                                {/* Avatar */}
                                {a.photoURL ? (
                                    <img src={a.photoURL} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0 ring-2 ring-emerald-100" />
                                ) : (
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                                        style={{ backgroundColor: cfg.color || '#10b981' }}>
                                        {(a.displayName || a.email || '?')[0].toUpperCase()}
                                    </div>
                                )}

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-gray-800">{a.displayName || 'Sin nombre'}</h4>
                                    <p className="text-xs text-gray-500">{a.email}</p>

                                    {/* Days */}
                                    <div className="flex gap-1 mt-1.5 flex-wrap">
                                        {DAYS.map(d => {
                                            const active = (cfg.availableDays || ALL_DAYS.slice(0, 5)).includes(d.val);
                                            return (
                                                <span key={d.val} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${active
                                                    ? 'bg-emerald-100 text-emerald-700'
                                                    : 'bg-gray-100 text-gray-300 line-through'}`}>
                                                    {d.label}
                                                </span>
                                            );
                                        })}
                                    </div>

                                    {/* Slots */}
                                    <div className="flex gap-1 mt-1 flex-wrap">
                                        {(cfg.availableSlots || ['18:00', '19:00']).map(s => (
                                            <span key={s} className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full border border-emerald-200">
                                                <FiClock size={9} /> {slotLabel(s)}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* MC/day badge */}
                                <div className="text-center px-4 flex-shrink-0">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald-50 border-2 border-emerald-200">
                                        <p className="text-xl font-bold text-emerald-600">{cfg.maxMcPerDay ?? 2}</p>
                                    </div>
                                    <p className="text-[10px] text-gray-500 mt-1">MC/día</p>
                                </div>

                                {/* Edit */}
                                <button onClick={() => openEdit(a)}
                                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition flex-shrink-0" title="Configurar">
                                    <FiEdit2 size={15} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Edit Modal */}
            {editingUser && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 text-white flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {editingUser.photoURL
                                    ? <img src={editingUser.photoURL} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-white/30" />
                                    : <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center font-bold">
                                        {(editingUser.displayName || '?')[0].toUpperCase()}
                                    </div>
                                }
                                <div>
                                    <p className="font-bold text-sm leading-tight">{editingUser.displayName || 'Sin nombre'}</p>
                                    <p className="text-[11px] opacity-75">{editingUser.email}</p>
                                </div>
                            </div>
                            <button onClick={() => setEditingUser(null)} className="p-1 hover:bg-white/20 rounded-lg transition">
                                <FiX size={18} />
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* Max MC per day */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Máx. Masterclasses por día
                                </label>
                                <div className="flex items-center gap-3">
                                    <button type="button"
                                        onClick={() => setEditForm(p => ({ ...p, maxMcPerDay: Math.max(1, p.maxMcPerDay - 1) }))}
                                        className="w-9 h-9 rounded-xl border-2 border-gray-200 font-bold text-gray-600 hover:border-emerald-400 hover:text-emerald-600 transition text-lg">−</button>
                                    <span className="text-2xl font-bold text-emerald-600 w-8 text-center">{editForm.maxMcPerDay}</span>
                                    <button type="button"
                                        onClick={() => setEditForm(p => ({ ...p, maxMcPerDay: Math.min(5, p.maxMcPerDay + 1) }))}
                                        className="w-9 h-9 rounded-xl border-2 border-gray-200 font-bold text-gray-600 hover:border-emerald-400 hover:text-emerald-600 transition text-lg">+</button>
                                    <span className="text-xs text-gray-500">masterclasses/día (máx 5)</span>
                                </div>
                            </div>

                            {/* Available days */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                                    <FiCalendar size={14} className="text-emerald-600" />
                                    Días disponibles
                                </label>
                                <div className="flex gap-2 flex-wrap">
                                    {DAYS.map(day => {
                                        const selected = editForm.availableDays.includes(day.val);
                                        return (
                                            <button key={day.val} type="button" onClick={() => toggleDay(day.val)}
                                                className={`px-3 py-2 rounded-xl text-sm font-bold border-2 transition ${selected
                                                    ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                                                    : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                                                {selected && <FiCheckCircle className="inline mr-1" size={12} />}
                                                {day.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Available slots */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                                    <FiClock size={14} className="text-emerald-600" />
                                    Franjas horarias disponibles
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {ALL_SLOTS.map(slot => {
                                        const selected = editForm.availableSlots.includes(slot.val);
                                        return (
                                            <button key={slot.val} type="button" onClick={() => toggleSlot(slot.val)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-semibold border-2 transition ${selected
                                                    ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                                                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                                                {selected && <FiCheckCircle size={13} />}
                                                {slot.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Color */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Color de identificación</label>
                                <input type="color" value={editForm.color}
                                    onChange={e => setEditForm(p => ({ ...p, color: e.target.value }))}
                                    className="w-full h-10 border rounded-xl cursor-pointer p-1" />
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-1">
                                <button type="button" onClick={() => setEditingUser(null)}
                                    className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 font-semibold hover:bg-gray-50 text-sm transition">
                                    <FiX className="inline mr-1" /> Cancelar
                                </button>
                                <button type="button" onClick={saveEdit}
                                    className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 shadow text-sm transition">
                                    <FiSave className="inline mr-1" /> Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AsesoresPage;
